/**
 * Redaction of anything that looks like a person, on the way in.
 *
 * ## Why the server does this even though the browser already can
 *
 * The tracking script decides what part of a URL is safe to keep, and it does
 * so in the visitor's browser. That is the right place, and it is not a
 * sufficient place, for two reasons that are both about who is allowed to
 * write:
 *
 *  - the public ingest path takes no credential beyond a key that sits in the
 *    page source of every customer site by design, so anyone at all can POST a
 *    raw URL. The server has to be the trust boundary;
 *  - scripts older than the current rules keep running on real sites for as
 *    long as the customer takes to redeploy — which we do not control and
 *    cannot schedule. Redacting at ingestion protects those deployments
 *    retroactively, with zero customer action.
 *
 * ## Why this matters more on Panel than it did on the fork
 *
 * Panel's whole claim is that it cannot follow anyone from one day to the next.
 * A visitor id that rotates nightly is worth nothing if the row next to it
 * holds `/reset-password?token=…` or `/orders/jean.dupont@example.com`. A URL
 * is the most reliable place a personal identifier leaks into analytics, and it
 * arrives from a page we do not own. This is where that is stopped.
 *
 * ## The trade in the heuristic
 *
 * It over-redacts, deliberately. `/products/iphone-15-pro-max` is not a person
 * and still becomes `/products/:id`. That costs a little reporting detail; the
 * other direction costs someone their privacy, and only one of the two is
 * recoverable.
 */

/**
 * Is this path segment an identifier rather than a route name?
 *
 * **This rule has a second home in the browser script, and the two must not
 * drift.** `__fixtures__/scrub-cases.json` is the table both sides are tested
 * against, and it is what catches a change made on one side alone. Do not
 * "improve" one copy by itself.
 *
 * One alternation rather than five tests because the browser copy pays for
 * every byte of it. In order: a bare number (`/patients/44812`); a UUID
 * (`/orders/3f2504e0-…`); an `@` with a dot after it, wherever it sits
 * (`/account/john.doe@mail.example`); and, last, anything over 16 characters
 * mixing letters and digits, which is the shape of a token or a JWT
 * (`/reset-password/eyJhbGciOiJIUzI1NiJ9.…`).
 */
export const IDENTIFIER_PATTERN =
  /^\d+$|^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$|@[^@/]*\.|^(?=.{17,}$)(?=.*[a-z])(?=.*\d)/i;

const ID = ':id';

/**
 * Path only: query string and fragment dropped, identifying segments replaced
 * with `:id`, the result clamped to `max`.
 *
 * Same semantics as the browser copy, including the null returns: a
 * non-string, an empty string and a string that is nothing but a query
 * (`?a=b`) all mean "no path worth storing" rather than "the empty path".
 */
export function scrubPath(raw: unknown, max: number): string | null {
  if (typeof raw !== 'string') return null;
  const trimmed = raw.trim();
  if (trimmed.length === 0) return null;
  const head = trimmed.split(/[?#]/)[0] || '';
  if (head.length === 0) return null;
  const parts = (head.charAt(0) === '/' ? head : `/${head}`).split('/');
  for (let i = 0; i < parts.length; i++) {
    if (IDENTIFIER_PATTERN.test(parts[i] as string)) parts[i] = ID;
  }
  return parts.join('/').slice(0, max);
}

/**
 * The clamp applied to a path found inside free text. Mirrors
 * `MAX_PATH_LENGTH` in `packages/api/src/services/observation.service.ts`: the
 * same reason applies, and a stack frame has no claim to a longer path than a
 * page view does.
 */
const MAX_EMBEDDED_PATH_LENGTH = 120;

/**
 * Every http(s) URL in a blob of text. Stops at whitespace and at the closing
 * delimiters stacks actually use — `)` above all, because V8 writes
 * `at submit (https://…:12:34)` and swallowing the paren would swallow the
 * frame.
 */
const URL_TOKEN = /(?:https?|wss?):\/\/[^\s)'"<>]+/gi;

/**
 * An e-mail address anywhere in free text.
 *
 * The one direct identifier that no URL rule can reach, because it does not
 * need a URL to travel: `throw new Error("could not reach " + user.email)` puts
 * it in `m` as prose, and `mailto:` puts it there with a scheme that is not
 * `http`. Both end up in `ingest_errors.message`, which the vendor reads.
 *
 * The domain must carry a dot, which is what keeps `@media`, `@scope` and a
 * lone `@` in a CSS selector — all of them ordinary in a front-end stack trace
 * — from being mistaken for addresses and mangled.
 */
const EMAIL = /[A-Za-z0-9._%+-]+@[A-Za-z0-9-]+(?:\.[A-Za-z0-9-]+)+/g;

/**
 * A trailing `:line` or `:line:column`. Every engine appends one inside the
 * URL token, so it is captured by {@link URL_TOKEN} and must be detached
 * before parsing and reattached after — see {@link scrubUrlsInText}.
 */
const LINE_COLUMN_SUFFIX = /:\d+(?::\d+)?$/;

/**
 * Redact every http(s) URL inside free text, keeping the text around it.
 *
 * Free text arrives as whatever a page produced, and it routinely contains a
 * URL that the path rule above never saw:
 * `TypeError: Failed to fetch https://api.shop.example/cart?token=abc123`.
 * Each URL becomes `origin` + its scrubbed pathname; query string and fragment
 * are dropped, exactly as they are for a page path.
 *
 * ## The two traps, both load-bearing
 *
 * **The line:column suffix is inside the token.** `[^\s)'"<>]+` cannot stop
 * before `:12:34`, so a naive parse treats it as part of the URL and the
 * rewritten frame loses the only two numbers in it that a developer reads. So
 * the suffix is detached first, and reattached to the result — but only when
 * it sits after the start of the path, or `https://localhost:3000` would lose
 * its port to the same rule.
 *
 * **A URL that will not parse is left alone.** A truncated or mangled token is
 * still evidence; destroying the frame to be safe would trade a privacy risk we
 * can bound for a debugging loss we cannot. The `catch` returns the token
 * untouched, and the `?`-assertions in the integration suite are what keep that
 * honest.
 */
export function scrubUrlsInText(raw: string): string {
  return scrubEmails(raw.replace(URL_TOKEN, (token) => {
    // A `:1234` is a port until a path has started; after that it is a line
    // number. `indexOf('/', …)` past the scheme is where the path starts.
    const authorityStart = token.indexOf('://') + 3;
    const pathStart = token.indexOf('/', authorityStart);
    const suffixMatch = pathStart === -1 ? null : LINE_COLUMN_SUFFIX.exec(token);
    const suffix = suffixMatch !== null && suffixMatch.index > pathStart ? suffixMatch[0] : '';
    const head = suffix.length > 0 ? token.slice(0, -suffix.length) : token;

    let url: URL;
    try {
      url = new URL(head);
    } catch {
      return token;
    }
    // No path in the original means no path in the result: appending the `/`
    // that `new URL` invents would rewrite text nobody asked us to rewrite.
    const path = pathStart === -1 ? '' : (scrubPath(url.pathname, MAX_EMBEDDED_PATH_LENGTH) ?? '');
    return `${url.origin}${path}${suffix}`;
  }));
}

/** Replace every e-mail address with {@link ID}. Runs after the URL pass, so an
 * address that was authority credentials is already gone and this only has to
 * catch the ones written as text. */
function scrubEmails(text: string): string {
  return text.replace(EMAIL, ID);
}
